/**
 * The Coach — a pure analyzer over engine state, the mahjong sibling of
 * sudoku's `HintController`. It never touches canvas or the DOM; it returns
 * typed `CoachAdvice` (i18n key + params) that the DOM `CoachPanel` renders.
 *
 * Discard ranking REUSES the bot's `usefulness` heuristic (the single source of
 * "how worth keeping is this tile") and layers a reason-classification pass on
 * top, so the coach can EXPLAIN the pick rather than only score it. Selection
 * is deterministic (no RNG): ties break by the canonical sort order.
 */

import {
  COPIES_PER_KIND,
  RANKS,
  countByKey,
  sortTiles,
  tileKey,
  type Seat,
  type Tile,
} from '../game/types.js';
import { usefulness } from '../ai/EasyStrategy.js';
import { claimsFor, type MahjongState } from '../game/GameState.js';
import type { SeatClaims } from '../game/Claims.js';
import { analyzeHand } from './handShape.js';
import {
  CLAIM_REASON,
  DISCARD_REASON,
  type ClaimAdvice,
  type CoachAdvice,
  type DiscardAdvice,
} from './types.js';

export { analyzeHand } from './handShape.js';

const TERMINAL_LOW: number = RANKS[0] ?? 1;
const TERMINAL_HIGH: number = RANKS[RANKS.length - 1] ?? 9;

/** Which reason code explains discarding `tile` (excess copies handled upstream). */
function classifyReason(tile: Tile): string {
  if (tile.kind === 'honor') return DISCARD_REASON.loneHonor;
  if (tile.rank === TERMINAL_LOW || tile.rank === TERMINAL_HIGH) return DISCARD_REASON.terminal;
  return DISCARD_REASON.noConnection;
}

/**
 * The tile the coach would part with, plus WHY. A 4th copy of a kind is always
 * a safe spare (a set needs only three), so that's flagged first; otherwise the
 * least-useful tile per `usefulness` is chosen and its reason classified.
 */
export function suggestDiscard(hand: readonly Tile[]): DiscardAdvice {
  const sorted = sortTiles(hand);
  const first = sorted[0];
  if (first === undefined) throw new Error('cannot advise a discard for an empty hand');

  const counts = countByKey(hand);
  const excess = sorted.find((tile) => (counts.get(tileKey(tile)) ?? 0) >= COPIES_PER_KIND);
  if (excess !== undefined) {
    return {
      tile: excess,
      reasonKey: DISCARD_REASON.excessCopy,
      reasonParams: { tile: tileKey(excess) },
    };
  }

  let worst = first;
  let worstScore = usefulness(first, hand);
  for (const tile of sorted) {
    const score = usefulness(tile, hand);
    if (score < worstScore) {
      worstScore = score;
      worst = tile;
    }
  }
  return {
    tile: worst,
    reasonKey: classifyReason(worst),
    reasonParams: { tile: tileKey(worst) },
  };
}

/**
 * One advice entry per claim the local seat can make on the open discard, or
 * null when it has no stake. `seat`/`state` are unused by iteration-1 advice
 * but keep the seam symmetric with the AI's `chooseClaim`.
 */
export function adviseOnClaims(
  _seat: Seat,
  _state: MahjongState,
  claims: SeatClaims,
): ClaimAdvice[] | null {
  const advice: ClaimAdvice[] = [];
  if (claims.canRon) advice.push({ claimType: 'ron', reasonKey: CLAIM_REASON.ron });
  if (claims.canPung) advice.push({ claimType: 'pung', reasonKey: CLAIM_REASON.pung });
  if (claims.chi.length > 0) advice.push({ claimType: 'chi', reasonKey: CLAIM_REASON.chi });
  return advice.length > 0 ? advice : null;
}

/** Compose the full advice bundle for `localSeat` against the current state. */
export function buildCoachAdvice(state: MahjongState, localSeat: Seat): CoachAdvice {
  const hand = state.hands[localSeat] ?? [];
  const melds = state.melds[localSeat] ?? [];
  const handShape = analyzeHand(hand, melds);

  const discardAdvice =
    state.phase === 'awaiting-discard' && state.turn === localSeat && hand.length > 0
      ? suggestDiscard(hand)
      : null;

  let claimAdvice: ClaimAdvice[] | null = null;
  if (state.phase === 'claim-window') {
    const seatClaims = claimsFor(state).find((c) => c.seat === localSeat);
    if (seatClaims !== undefined) claimAdvice = adviseOnClaims(localSeat, state, seatClaims);
  }

  return { handShape, discardAdvice, claimAdvice };
}
